// ─────────────────────────────────────────────────────────
// Formatierung — Geld, Zahlen, Datum. EINE Quelle für die ganze App.
// ─────────────────────────────────────────────────────────
// Vorher gab es sieben Geld-Formatierer, die DREI verschiedene Ergebnisse
// lieferten (1'234'567.50 / 1’234’567.50 / 1234567,50) und neun Datums-Helfer.
//
// ── Zwei Regeln, die man leicht verwechselt ──
//
// 1) SCHWEIZER KONVENTION ist an das LAND gebunden, nicht an die Sprache.
//    Geld, Zahlen und numerische Daten verwenden de-CH ausdrücklich. Ein
//    Locale-Tag wie fr-CH garantiert KEINEN Punkt als Dezimaltrenner; fa-CH
//    verwendet sogar standardmässig den persischen Kalender. Ausgeschriebene
//    Monats-/Wochentagsnamen und relative Zeiten folgen der App-Sprache,
//    mit gregorianischem Kalender und lateinischen Ziffern.
//
// 2) DER APOSTROPH: Intl liefert für de-CH U+2019 (’). Das Rechnungs-PDF
//    (backend render.service.ts) baut den Tausendertrenner von Hand mit dem
//    ASCII-Apostroph ('). Der Kunde bekommt das PDF — also gewinnt das PDF,
//    sonst steht auf dem Bildschirm ein anderer Betrag als auf der Rechnung.
//    Wir rechnen mit Intl (korrekte Gruppierung) und normalisieren danach.
// i18n/index.js ist Vanilla-JS, hat aber seit i18n/index.d.ts echte Typen —
import { getLanguage } from '../i18n/index.js';

/** Was hier hereindarf: alles, woraus sich sinnvoll eine Zahl machen lässt. */
type Numberish = number | string | null | undefined;
/** Datum als Date, ISO-String oder nichts. */
type Dateish = Date | string | null | undefined;

// Der Tausendertrenner, den auch das Rechnungs-PDF verwendet.
const GROUP_SEP = "'";

/** App-Sprache → Schweizer Locale. Muss alle aktiven App-Sprachen abdecken. */
const SWISS_LOCALES: Readonly<Record<string, string>> = Object.freeze({
  de: 'de-CH',
  en: 'en-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  es: 'es-CH',
  pt: 'pt-CH',
  tr: 'tr-CH',
  sq: 'sq-CH',
  sr: 'sr-CH',
  hr: 'hr-CH',
  ar: 'ar-CH',
  ru: 'ru-CH',
  uk: 'uk-CH',
  pl: 'pl-CH',
  nl: 'nl-CH',
  fa: 'fa-CH',
  ku: 'ku-CH',
});

function locale(): string {
  return `${SWISS_LOCALES[getLanguage()] ?? 'de-CH'}-u-ca-gregory-nu-latn`;
}

/**
 * Die aktive Locale — nur für Sonderfälle, die keine Funktion hier abdeckt
 * (z.B. ein 2-stelliges Jahr). Erste Wahl bleiben die Funktionen unten:
 * so hartkodiert niemand mehr 'de-CH' und der Sprachwechsel greift überall.
 */
export function formatLocale(): string {
  return locale();
}

/** A real instant in its source reporting zone; includes date and UTC offset so
 * a next-day midnight or repeated DST hour is never shown as the same instant. */
export function dateTimeInZone(value: Dateish, timeZone: string): string {
  const instant = toDate(value);
  if (!instant) return '';
  return new Intl.DateTimeFormat(locale(), { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone, timeZoneName: 'shortOffset' }).format(instant);
}

/** Intl-Ergebnis auf den Apostroph des PDFs bringen (U+2019/U+00A0 → '). */
function normalizeGroups(s: string): string {
  return s.replace(/[’  ]/g, GROUP_SEP);
}

function toNumber(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

// ── Zahlen ────────────────────────────────────────────────

/** Zahl mit Schweizer Tausendertrennung. `1234.5` → `1'234.50` */
export function num(value: Numberish, { decimals = 2 }: { decimals?: number } = {}): string {
  return normalizeGroups(
    new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(toNumber(value)),
  );
}

/** Prozent. `8.1` → `8.10 %` */
export function percent(value: Numberish, { decimals = 2 }: { decimals?: number } = {}): string {
  return num(value, { decimals }) + ' %';
}

// ── Geld ──────────────────────────────────────────────────

/**
 * Betrag OHNE Währung — für Tabellenspalten, die „CHF" schon in der Kopfzeile haben.
 * `1234.5` → `1'234.50`
 */
export function amount(value: Numberish, { decimals = 2 }: { decimals?: number } = {}): string {
  return num(value, { decimals });
}

/**
 * Betrag MIT Währung. `1234.5` → `CHF 1'234.50`
 * Währung ist ein Parameter, kein Fixwert: Schweizer Betriebe rechnen an der
 * Grenze auch in EUR, und die Rechnung müsste das dann mitführen.
 */
export function money(value: Numberish, { currency = 'CHF', decimals = 2 }: { currency?: string; decimals?: number } = {}): string {
  return `${currency} ${num(value, { decimals })}`;
}

/** Exact reporting decimals: round to cents half away from zero without an
 * IEEE-754 conversion. Missing or malformed values must not become zero. */
export function moneyExact(value: string | null | undefined, { currency = 'CHF' }: { currency?: string } = {}): string {
  const match = typeof value === 'string' ? /^(-?)(\d+)(?:\.(\d+))?$/.exec(value) : null;
  if (!match) return '—';
  const fraction = (match[3] || '').padEnd(3, '0');
  const cents = BigInt(match[2]) * 100n + BigInt(fraction.slice(0, 2)) + (fraction[2] >= '5' ? 1n : 0n);
  const whole = String(cents / 100n).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEP);
  return `${currency} ${match[1] && cents !== 0n ? '-' : ''}${whole}.${String(cents % 100n).padStart(2, '0')}`;
}

// ── Datum ─────────────────────────────────────────────────

function toDate(value: Dateish): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // Reines Datum (YYYY-MM-DD) MUSS lokal gelesen werden: new Date('2026-07-16')
  // ist laut Spec UTC-Mitternacht — westlich von Greenwich zeigt das den
  // Vortag an. Betrifft alles, was ein Datum ohne Uhrzeit speichert
  // (Feiertage, Fälligkeiten, Projekt-Zeiträume).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const d = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return d.getFullYear() === Number(dateOnly[1])
      && d.getMonth() === Number(dateOnly[2]) - 1
      && d.getDate() === Number(dateOnly[3]) ? d : null;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `16.07.2026` — leerer String bei ungültigem/fehlendem Wert (nie „Invalid Date"). */
export function date(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * `Heute` / `Gestern` / `Today` / `Yesterday` — oder `null`, wenn der Wert weder heute
 * noch gestern ist (dann soll der Aufrufer ein absolutes Datum zeigen).
 *
 * Gerechnet wird in KALENDERTAGEN, nicht in 24-Stunden-Schritten: 23:00 gestern und
 * 01:00 heute liegen zwei Stunden auseinander, sind aber verschiedene Tage.
 */
export function relativeDay(value: Dateish): string | null {
  const d = toDate(value);
  if (!d) return null;
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);
  if (days !== 0 && days !== -1) return null;
  const s = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' }).format(days, 'day');
  // Intl liefert klein ("heute"/"gestern"); hier steht das Wort am Label-Anfang.
  return s.charAt(0).toLocaleUpperCase(locale()) + s.slice(1);
}

/**
 * `vor 3 Tagen` / `3 days ago` — relative Zeit in der aktuellen Sprache.
 *
 * Bewusst über `Intl.RelativeTimeFormat` statt über Übersetzungs-Keys: Wortlaut UND
 * Pluralregeln stecken schon in der Locale-Datenbank. Von Hand ('vor ' + n + ' Tagen')
 * hiesse, sie je Sprache nachzubauen — und genau das war vorher der Fall, inklusive
 * eines eigenen `n === 1`-Zweigs.
 *
 * `null` bei ungültigem Wert oder wenn die Angabe zu alt ist (>= `maxDays`) — dann soll
 * der Aufrufer ein absolutes Datum zeigen, nicht „vor 143 Tagen".
 */
export function relativeTime(value: Dateish, { maxDays = 7 }: { maxDays?: number } = {}): string | null {
  const d = toDate(value);
  if (!d) return null;

  const diffMs = d.getTime() - Date.now(); // negativ = Vergangenheit
  const minutes = Math.round(diffMs / 60000);
  const absMin = Math.abs(minutes);

  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  if (absMin < 1) return rtf.format(0, 'minute'); // „jetzt" / „now"
  if (absMin < 60) return rtf.format(minutes, 'minute');

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');

  const days = Math.round(hours / 24);
  if (Math.abs(days) < maxDays) return rtf.format(days, 'day');

  return null;
}

/** `16.07.2026, 14:30` */
export function dateTime(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString('de-CH', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** `14:30` */
export function time(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

/** `16. Juli 2026` / `16 July 2026` — hier wirkt die Sprache wirklich. */
export function dateLong(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

/** `Juli` / `July` — ausgeschriebener Monat, folgt der Sprache. */
export function monthLong(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale(), { month: 'long' });
}

/** Localized short month for chart axes. Date-only values retain their month. */
export function monthShort(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale(), { month: 'short' });
}

/** Month and year keep multi-year project timelines unambiguous. */
export function monthYear(value: Dateish, { short = false }: { short?: boolean } = {}): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString(locale(), { month: short ? 'short' : 'long', year: 'numeric' });
}

/** `16.07.` — Tag und Monat, z.B. für kompakte Listen. */
export function dateShort(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
}

/** YYYY-MM-DD (ISO) — für <input type="date">, NICHT für Anzeige. Ohne Zeitzonen-Drift. */
export function isoDate(value: Dateish): string {
  const d = toDate(value);
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Mo, Di, … in der UI-Sprache — Montag zuerst (Kalender-Kopfzeile). */
export function weekdayShortNames(): string[] {
  const formatter = new Intl.DateTimeFormat(locale(), { weekday: 'short' });
  return Array.from({ length: 7 }, (_, day) => formatter.format(new Date(2026, 0, 5 + day)));
}

/** Monday first; compute on each render so labels follow a language switch. */
export function weekdayNames(): string[] {
  const formatter = new Intl.DateTimeFormat(locale(), { weekday: 'long' });
  return Array.from({ length: 7 }, (_, day) => formatter.format(new Date(2026, 0, 5 + day)));
}

/** Local ISO-like value for datetime-local controls, never for display. */
export function dateTimeInput(value: Dateish): string {
  const date = toDate(value); if (!date) return '';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** File size for attachments: «840 B», «12 KB», «3.4 MB», «1.25 GB» (Swiss number format, like num). */
export function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${num(bytes, { decimals: 0 })} B`;
  if (bytes < 1024 * 1024) return `${num(bytes / 1024, { decimals: 0 })} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${num(bytes / (1024 * 1024), { decimals: 1 })} MB`;
  return `${num(bytes / (1024 * 1024 * 1024), { decimals: 2 })} GB`;
}
